import React, { useContext, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQueryParams } from "utils/hooks/useQueryParams";
import axios from "axios";
import Email from "common/EmailV2";
import { BreakpointContext } from "components/common";
import { useQueryParamsAsJson } from "@tomocredit/common-fe/utils";
import { isValidEmail } from "@tomocredit/common-fe/utils/validation";
import { LEAD_SOURCES } from "@tomocredit/common-fe/consts/consts";
import {
  currMobileBrowserAccepted,
  fromWebview,
  isMobile,
  isTomoEmail,
  referralParam,
  utmParams,
} from "utils/userAgentInfo";
import Guard from "images/Guard2.svg";
import ExperianLogo from "images/experian-business-logo.svg";
import EquifaxLogo from "images/equifax-business-logo.svg";
import { Stack } from "@mui/material";
import CheckMark from "@tomocredit/common-fe/components/CheckMark";

const generateLoginLink = (queryParameters) => {
  return `${process.env.REACT_APP_DOMAIN}/dashboard/login${
    queryParameters.size ? `?${queryParameters.toString()}` : ""
  }`;
};

const BureauLogo = () => {
  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="center"
      alignItems="center"
      sx={{
        marginY: "1rem",
      }}
    >
      <img alt="equifax_logo" src={EquifaxLogo} style={{ height: "12.59px", width: "63.57px",  opacity: "50%" }} />
      <img alt="experian_logo" src={ExperianLogo} style={{ height: "23.75px", width: "71.52px",  opacity: "50%" }} />
    </Stack>
  );
};

const BureauLogoDesktop = () => {
  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="start"
      alignItems="center"
      sx={{
        marginY: "1rem",
      }}
    >
      <img alt="equifax_logo" src={EquifaxLogo} style={{ height: "16.26px", width: "82.12px",  opacity: "80%" }} />
      <img alt="experian_logo" src={ExperianLogo} style={{ height: "30.67px", width: "92.38px",  opacity: "80%" }} />
    </Stack>
  );
};

const LandingForm = () => {
  const { isBelow } = useContext(BreakpointContext);
  const belowMd = isBelow("md");
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { capturedUrlParams, rawUrlParams } = useQueryParamsAsJson(["email"]);

  const withQueryParams = useQueryParams();

  const sendOldEmailVerification = !!(
    (isMobile() && (utmParams.source || !currMobileBrowserAccepted())) ||
    fromWebview
  );

  const handleSubmit = async (email) => {
    if (loading) {
      return;
    }
    setError(null);
    if (!email) {
      setError("Email address is required");
      return;
    }
    if (!isValidEmail(email)) {
      setError("Invalid email address");
      return;
    }
    setLoading(true);

    try {
      const userPayload = {
        withReferralCode: referralParam,
        sendOldEmailVerification,
        userId: email,
        utm: utmParams,
        source: LEAD_SOURCES.KOSMOFI_LANDING,
        capturedUrlParams,
      };
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/users/newUserFromLanding`,
        userPayload,
      )
        .then((data) => {
          const { boostEnrolled, softTerminated } = data?.data ?? {};
          if (boostEnrolled && !softTerminated) {
            rawUrlParams.set("email", email);
            window.location.assign(generateLoginLink(rawUrlParams));
            return;
          }
          history.push(withQueryParams("/boost-pricing", { email }));
        });
    } catch (err) {
      setError("Could not communicate with server. Please try again later");
      return;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`text-white px-6 ${isBelow("sm") ? "w-full" : ""}`}>
      {!belowMd &&
        <p
          className="font-bold"
          // eslint-disable-next-line no-nested-ternary
          style={{ fontSize: isBelow("sm") ? 36 : 63, letterSpacing: -0.25, lineHeight: 1.1 }}
        >
          <span className="">Build Credit, Grow your business</span>
        </p>
      }
      {!belowMd &&
        <div className="py-2 text-lg md:text-2xl w-1/2">
          <CheckMark className="my-4" color="boost">
            <span className="ml-3">
              Up to <span className="font-bold"> $30,000 credit line</span>
            </span>
          </CheckMark>
          <CheckMark className="my-4" color="boost">
            <span className="ml-3">
              <span className="font-bold">105+ </span>
              <span>credit boost</span>
            </span>
          </CheckMark>
          <CheckMark className="my-4" color="boost">
            <span className="ml-3 relative">
              No credit checks. <span className="font-bold">Ever.</span>
            </span>
          </CheckMark>
        </div>
      }
      {belowMd && <BureauLogo />}
      <div style={{ maxWidth: 393 }}>
        <Email
          buttonText="Get Started"
          handleSubmit={handleSubmit}
          componentClassName="flex-col sm:flex-row space-y-4 sm:space-y-0 mt-2"
          inputClassName="w-full rounded-md sm:rounded-r-none border border-neutral-500 sm:border-r-0 placeholder-gray-600 text-black bg-white"
          buttonClassName="bg-boost-button-purple text-white w-full sm:w-auto py-2 text-white rounded-md sm:rounded-l-none font-medium bg-black-bg hover:bg-neutral-600 active:bg-neutral-700 px-4 border border-boost-button-purple hover:border-neutral-600 active:border-neutral-700"
        />
        {error && (
          <p className="text-sm text-red-500 text-left mt-1">{error}</p>
        )}
      </div>
      <div className="mt-8 text-base flex items-center text-left text-boost-price-green whitespace-normal md:whitespace-nowrap w-full">
        <img src={Guard} alt="" className="inline mr-2" style={{ width:"auto", height: "1.5rem" }}/>
        <span>
          Trusted by <span className="font-bold">4 million+</span>{" "}
          people building credit with TomoBoost
        </span>
      </div>
      {!belowMd && <BureauLogoDesktop />}
    </div>
  );
};

export default LandingForm;
